import React, { useState } from "react";
import { Search, Mail, Package, Sparkles, Calendar, Wallet, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const fmt = (d) => (d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "—");
const eur = (cents) => (cents == null ? "" : `${(cents / 100).toFixed(2)} €`);

const Section = ({ icon: Icon, title, items, render, testid }) => (
    <div className="mt-5" data-testid={testid}>
        <div className="flex items-center gap-2 text-sm font-medium text-primary mb-2">
            <Icon className="w-4 h-4" /> {title} ({items.length})
        </div>
        {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun</p>
        ) : (
            <ul className="space-y-1 text-sm">{items.map((it) => <li key={it.id} className="border-l-2 border-primary/30 pl-3">{render(it)}</li>)}</ul>
        )}
    </div>
);

const ResetButton = ({ user }) => {
    const [busy, setBusy] = useState(false);
    const send = async () => {
        if (!window.confirm(`Envoyer à ${user.email} un lien pour choisir un nouveau mot de passe (valable 48 h) ?`)) return;
        setBusy(true);
        try {
            await http.post(`/admin/accounts/${user.id}/send-reset`);
            toast.success(`Lien envoyé à ${user.email}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };
    return (
        <Button variant="outline" size="sm" className="rounded-full mt-4" onClick={send} disabled={busy} data-testid={`account-send-reset-${user.id}`}>
            <KeyRound className="w-4 h-4 mr-2" /> {busy ? "Envoi…" : "Envoyer un lien de nouveau mot de passe"}
        </Button>
    );
};

const AccountCard = ({ data }) => {
    const u = data.user;
    return (
        <div className="rounded-3xl border bg-card p-6" data-testid={`account-card-${u.id}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                    <div className="font-serif text-2xl" data-testid="account-name">{[u.first_name, u.last_name].filter(Boolean).join(" ") || u.name || "—"}</div>
                    <div className="text-sm text-muted-foreground" data-testid="account-email">{u.email}</div>
                </div>
                <div className="text-sm text-right">
                    <div>Rôle : <b>{u.role || "user"}</b></div>
                    <div>Abonnement : <b data-testid="account-subscription">{u.active_subscription || "aucun"}</b></div>
                    <div>Inscrit le {fmt(u.created_at)}</div>
                    <div>Infolettre : {data.newsletter ? "oui" : "non"}</div>
                </div>
            </div>
            <ResetButton user={u} />
            <Section icon={Package} title="Programmes / packs" items={data.packs} testid="account-packs"
                render={(p) => `${p.program_name || p.program_key || "Pack"} · ${p.sessions_remaining ?? "?"} séance(s) restante(s) · expire ${fmt(p.expires_at)}`} />
            <Section icon={Sparkles} title="Réservations de soins" items={data.bookings} testid="account-bookings"
                render={(b) => `${b.session_name || "Soin"} · ${fmt(b.slot_datetime)} · ${b.status}`} />
            <Section icon={Calendar} title="Inscriptions aux évènements" items={data.registrations} testid="account-registrations"
                render={(r) => `${r.event_title} · ${r.status} · ${fmt(r.created_at)}`} />
            <Section icon={Wallet} title="Paiements" items={data.transactions} testid="account-transactions"
                render={(t) => `${t.order_number || t.reference || ""} · ${t.item_name || t.kind} · ${eur(t.amount)} · ${t.payment_method || ""} · ${t.status} · ${fmt(t.created_at)}`} />
        </div>
    );
};

export default function AdminAccounts() {
    const [q, setQ] = useState("");
    const [res, setRes] = useState(null);
    const [loading, setLoading] = useState(false);

    const search = async (e) => {
        e.preventDefault();
        if (q.trim().length < 2) return;
        setLoading(true);
        try {
            const { data } = await http.get("/admin/accounts/search", { params: { q: q.trim() } });
            setRes(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="admin-accounts">
            <form onSubmit={search} className="flex gap-3 mb-6">
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="E-mail ou nom (ex. marie@gmail.com)" className="rounded-full" data-testid="accounts-search-input" />
                <Button type="submit" className="rounded-full" disabled={loading} data-testid="accounts-search-button">
                    <Search className="w-4 h-4 mr-2" /> {loading ? "Recherche…" : "Rechercher"}
                </Button>
            </form>
            {res && res.results.length === 0 && (
                <div className="rounded-3xl border bg-card p-6 text-sm" data-testid="accounts-no-result">
                    Aucun compte trouvé pour « {q} ».
                    {res.newsletter_only && (
                        <div className="mt-2 flex items-center gap-2" data-testid="accounts-newsletter-only">
                            <Mail className="w-4 h-4 text-primary" /> Cette adresse ({res.newsletter_only.email}) est inscrite uniquement à l'infolettre.
                        </div>
                    )}
                </div>
            )}
            <div className="space-y-6">{res?.results.map((r) => <AccountCard key={r.user.id} data={r} />)}</div>
        </div>
    );
}
